import { useEffect, useState } from "react";
import { Megaphone, Copy, ExternalLink, Sparkles, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";

const PLATFORMS = [
  { key: "meta", name: "Meta", sub: "Instagram + Facebook", url: "https://adsmanager.facebook.com/adsmanager/manage/campaigns" },
  { key: "google", name: "Google", sub: "Search + Shopping", url: "https://ads.google.com/aw/campaigns" },
  { key: "snapchat", name: "Snapchat", sub: "Snap Ads", url: "https://ads.snapchat.com/" },
];

const inputCls = "w-full bg-white border border-gray-300 rounded-lg text-sm px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E80071]/30 focus:border-[#E80071]";

export const CampaignKitTab = () => {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [offer, setOffer] = useState("VIRA10 — 10% off first order above ₹499");
  const [copy, setCopy] = useState({});
  const [creative, setCreative] = useState("");
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const selected = products.find((p) => p.id === productId);
  const siteUrl = window.location.origin;
  const trackedLink = (platform) => {
    const base = selected ? `${siteUrl}/product/${selected.id}` : siteUrl;
    return `${base}?utm_source=${platform}&utm_medium=paid&utm_campaign=vira_launch`;
  };

  const generateCopy = async (platform) => {
    setBusy((b) => ({ ...b, [platform]: true }));
    try {
      const { data } = await api.post("/admin/campaign-copy", { platform, product_id: productId, offer });
      setCopy((c) => ({ ...c, [platform]: data }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, [platform]: false }));
    }
  };

  const generateCreative = async () => {
    setBusy((b) => ({ ...b, creative: true }));
    try {
      const prompt = selected
        ? `Scroll-stopping social media ad for "${selected.name}" (${inr(selected.price)}) — product hero on a soft pink studio backdrop with petals and glow, premium beauty ad aesthetic, empty space at top for headline text`
        : "Scroll-stopping social media ad for a multi-brand beauty store sale — gift boxes, silk, pink and gold palette, premium aesthetic, empty space at top for headline text";
      const { data } = await api.post("/admin/generate-image", { prompt, kind: "banner" });
      setCreative(data.url);
      toast.success("Ad creative generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, creative: false }));
    }
  };

  const generateAll = () => {
    PLATFORMS.forEach((p) => generateCopy(p.key));
    generateCreative();
  };

  const copyText = (text, label) => navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));

  return (
    <div className="flex flex-col gap-6 max-w-[1100px]" data-testid="campaign-kit">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <p className="text-sm font-medium text-[#111827] mb-1 flex items-center gap-2"><Megaphone size={15} className="text-[#E80071]" /> Campaign Kit</p>
        <p className="text-xs text-gray-400 mb-4">One click builds everything an ad needs — AI creative, ad copy and a tracked link — then opens the platform's ads manager to paste it in. Fully automatic launch switches on when you connect the Meta / Google / Snapchat ads APIs.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="campaign-product" className={inputCls}>
            <option value="">Store-wide campaign</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {inr(p.price)}</option>)}
          </select>
          <input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Offer line (optional)" data-testid="campaign-offer" className={inputCls} />
          <button onClick={generateAll} data-testid="campaign-generate-all" className="flex items-center justify-center gap-2 bg-[#E80071] hover:bg-[#D00065] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm">
            <Sparkles size={14} /> Generate full kit
          </button>
        </div>
      </div>

      {creative && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="campaign-creative">
          <p className="text-sm font-medium text-[#111827] mb-3 flex items-center gap-2"><ImageIcon size={15} className="text-[#E80071]" /> Ad creative</p>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img src={creative} alt="Ad creative" className="w-full sm:w-80 rounded-lg border border-gray-100" />
            <div className="flex flex-col gap-2">
              <button onClick={() => copyText(window.location.origin + creative, "Creative URL")} data-testid="creative-copy-btn" className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm">
                <Copy size={13} /> Copy image URL
              </button>
              <a href={window.location.origin + creative} target="_blank" rel="noopener noreferrer" download data-testid="creative-download" className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm">
                <ExternalLink size={13} /> Download image
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {PLATFORMS.map((p) => {
          const c = copy[p.key];
          return (
            <div key={p.key} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col" data-testid={`platform-card-${p.key}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-[#111827]">{p.name}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">{p.sub}</p>
              </div>
              {c ? (
                <div className="flex-1 flex flex-col gap-3 mt-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">Headline</p>
                    <p className="text-sm text-gray-900 font-medium" data-testid={`copy-headline-${p.key}`}>{c.headline}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">Primary text</p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`copy-text-${p.key}`}>{c.primary_text}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">CTA: {c.cta}</span>
                    {(c.hashtags || []).map((h) => <span key={h} className="text-[10px] text-[#E80071]">{h}</span>)}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1">Tracked link</p>
                    <p className="text-xs text-gray-500 break-all">{trackedLink(p.key)}</p>
                  </div>
                  <div className="flex gap-2 mt-auto pt-3">
                    <button onClick={() => copyText(`Headline: ${c.headline}\n\n${c.primary_text}\n\nCTA: ${c.cta}\nLink: ${trackedLink(p.key)}\n${(c.hashtags || []).join(" ")}`, `${p.name} ad copy`)} data-testid={`copy-kit-${p.key}`} className="flex-1 flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                      <Copy size={12} /> Copy kit
                    </button>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`open-ads-${p.key}`} className="flex-1 flex items-center justify-center gap-2 bg-[#E80071] hover:bg-[#D00065] text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                      <ExternalLink size={12} /> Open {p.name} Ads
                    </a>
                  </div>
                </div>
              ) : (
                <button onClick={() => generateCopy(p.key)} disabled={busy[p.key]} data-testid={`generate-${p.key}`} className="mt-4 flex-1 flex items-center justify-center gap-2 border border-dashed border-gray-300 text-gray-500 hover:border-[#E80071] hover:text-[#E80071] px-3 py-6 rounded-lg text-sm transition-colors disabled:opacity-50">
                  <Sparkles size={14} /> {busy[p.key] ? "Writing…" : "Generate copy"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
